import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, ApiError, useApi, type Rec, type StockDetail } from '../api'
import { useIsMobile } from '../layout'
import { usePageTitle } from '../title'
import { daysSince, dirOf, fmtDate, fmtPct, fmtPrice, isStale, kindOf, signClass } from '../format'
import { Chart, indexAtOrAfter, sliceTimeframe, TimeframeTabs, type Marker, type Timeframe } from '../components/Chart'
import { Conv, DirPill, Empty, ErrorState, Icon, Loading, NotFound, PodAv } from '../components/primitives'

function counts(mentions: Rec[]) {
  return {
    buy: mentions.filter((m) => m.type === 'buy').length,
    hold: mentions.filter((m) => m.type === 'hold').length,
    sell: mentions.filter((m) => m.type === 'sell').length,
  }
}

function Legend({ mobile }: { mobile: boolean }) {
  const dot = (bg: string) => (mobile ? <i style={{ background: bg }} /> : <span style={{ width: 8, height: 8, borderRadius: '50%', background: bg }} />)
  return mobile ? (
    <div className="m-legend">
      <span>{dot('var(--accent)')}Buy</span>
      <span>{dot('var(--ink-3)')}Hold</span>
      <span>{dot('var(--down)')}Sell</span>
    </div>
  ) : (
    <div className="row g-12 mt-12" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
      <span className="row ai-c g-6">{dot('var(--accent)')} Buy</span>
      <span className="row ai-c g-6">{dot('var(--ink-3)')} Hold</span>
      <span className="row ai-c g-6">{dot('var(--down)')} Sell</span>
    </div>
  )
}

function PriceLine({ stock, size }: { stock: StockDetail['stock']; size: number }) {
  const stale = isStale(stock.last_ts)
  return (
    <div className="row ai-b g-8" style={{ marginTop: 6, flexWrap: 'wrap' }}>
      <span className="t-num" style={{ fontSize: size, fontWeight: 600 }}>{fmtPrice(stock.last_close)}</span>
      {stale && stock.last_ts ? (
        <span className="t-sm" style={{ color: 'var(--ink-3)' }} data-testid="stale">last priced {fmtDate(stock.last_ts)}</span>
      ) : (
        stock.day_change_pct != null && (
          <span className={`t-num text-${signClass(stock.day_change_pct) || 'up'}`} style={{ fontSize: 13 }}>
            {fmtPct(stock.day_change_pct, 2)} last close
          </span>
        )
      )}
    </div>
  )
}

export function Ticker() {
  const { ticker = '' } = useParams()
  const mobile = useIsMobile()
  const { data, error, retry } = useApi<StockDetail>(api.stock(ticker.toUpperCase()))
  usePageTitle(data?.stock.ticker ?? ticker.toUpperCase())
  const [tf, setTf] = useState<Timeframe>('3M')

  if (error instanceof ApiError && error.status === 404) {
    return <NotFound what={`Ticker ${ticker.toUpperCase()}`}><Link to="/stocks">All stocks</Link></NotFound>
  }
  if (error) return <ErrorState error={error} retry={retry} />
  if (!data) return <Loading />

  const { stock, mentions } = data
  const points = sliceTimeframe(data.prices, tf)
  const dates = points.map((p) => p.ts)
  const markers: Marker[] = mentions
    .map((m) => ({ index: indexAtOrAfter(dates, m.entry_ts), kind: kindOf(m.type), label: dirOf(m.type)?.[0] }))
    .filter((m) => m.index >= 0)
  const c = counts(mentions)
  const pct = (m: Rec) => (
    <span className={`num t-num ${m.pick_return_pct == null ? '' : m.pick_return_pct >= 0 ? 'text-up' : 'text-down'}`} style={{ width: 68, textAlign: 'right' }}>
      {fmtPct(m.pick_return_pct)}
    </span>
  )

  if (mobile) {
    return (
      <div data-testid="ticker-page">
        <span className="tick" style={{ fontSize: 28, fontWeight: 700 }}>{stock.ticker}</span>
        <div className="serif" style={{ fontSize: 22, marginTop: 2 }}>{stock.name}</div>
        <PriceLine stock={stock} size={26} />
        <div className="row ai-c g-6" style={{ marginTop: 12, flexWrap: 'wrap' }}>
          <span className="pill accent">{c.buy} BUY</span>
          <span className="pill hold">{c.hold} HOLD</span>
          <span className="pill down">{c.sell} SELL</span>
          <span className="t-sm" style={{ color: 'var(--ink-3)' }}>{mentions.length} mentions</span>
        </div>
        <div className="m-card" style={{ marginTop: 14 }}>
          <div className="t-xs" style={{ marginBottom: 6 }}>Price + podcast mentions</div>
          <div className="m-chart">
            <Chart values={points.map((p) => p.close)} dates={dates} markers={markers} height={220} variant="mobile" />
          </div>
          <Legend mobile />
          <TimeframeTabs value={tf} onChange={setTf} variant="mobile" />
        </div>
        <div className="m-sec">
          <h3>Mentions</h3>
          <span className="t-sm" style={{ color: 'var(--ink-3)' }}>{mentions.length} total</span>
        </div>
        {mentions.length === 0 ? (
          <Empty>No calls on {stock.ticker} yet.</Empty>
        ) : (
          <div className="m-list">
            {mentions.map((m) => (
              <Link key={m.id} to={`/rec/${m.id}`} className="m-row">
                <PodAv name={m.podcast_name} image={m.podcast_image_url} size="lg sq" />
                <div className="meta">
                  <span className="a">{m.podcast_name}</span>
                  <span className="b"><DirPill type={m.type} />{fmtDate(m.episode_pub_date)}</span>
                </div>
                {pct(m)}
                <span className="chev"><Icon name="chevron" size={16} /></span>
              </Link>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div data-testid="ticker-page">
      <div className="crumbs" style={{ marginBottom: 16 }}>
        <Link to="/">Home</Link>
        <span className="sep" aria-hidden>/</span>
        <Link to="/stocks">Stocks</Link>
        <span className="sep" aria-hidden>/</span>
        <span className="now">{stock.ticker}</span>
      </div>

      <div className="row ai-fs jc-sb g-20">
        <div>
          <span className="tick" style={{ fontSize: 30, fontWeight: 700 }}>{stock.ticker}</span>
          <h1 className="page-title" style={{ fontSize: 26, fontWeight: 500 }}>{stock.name}</h1>
          <PriceLine stock={stock} size={28} />
        </div>
        <div className="col g-8" style={{ alignItems: 'flex-end' }}>
          <div className="row g-6">
            <span className="pill accent">{c.buy} BUY</span>
            <span className="pill hold">{c.hold} HOLD</span>
            <span className="pill down">{c.sell} SELL</span>
          </div>
          <span className="t-sm">{mentions.length} podcast mentions</span>
        </div>
      </div>

      <div className="chart-wrap" style={{ marginTop: 22 }}>
        <div className="chart-head">
          <div className="col g-4">
            <span className="t-xs">Price + podcast mentions</span>
            <span className="t-sm">Markers show when each pick was made.</span>
          </div>
          <TimeframeTabs value={tf} onChange={setTf} variant="desktop" />
        </div>
        <Chart values={points.map((p) => p.close)} dates={dates} markers={markers} height={280} variant="desktop" />
        <Legend mobile={false} />
      </div>

      <div className="card mt-18">
        <div className="row ai-c jc-sb mb-12">
          <h3 className="serif" style={{ margin: 0, fontSize: 20, fontWeight: 500 }}>Mentions timeline</h3>
          <span className="t-sm">{mentions.length} total</span>
        </div>
        {mentions.length === 0 ? (
          <Empty>No calls on {stock.ticker} yet.</Empty>
        ) : (
          mentions.map((m) => (
            <Link key={m.id} to={`/rec/${m.id}`} className="mention" style={{ color: 'inherit', textDecoration: 'none' }}>
              <PodAv name={m.podcast_name} image={m.podcast_image_url} />
              <div className="meta">
                <div className="pods"><strong style={{ color: 'var(--ink)' }}>{m.podcast_name}</strong></div>
                <div className="when">{fmtDate(m.episode_pub_date)} · held {daysSince(m.episode_pub_date)}d</div>
              </div>
              <DirPill type={m.type} />
              <Conv n={m.conviction} />
              {pct(m)}
              <Icon name="chevron" size={14} />
            </Link>
          ))
        )}
      </div>
    </div>
  )
}
